import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "components/ui/button";
import { Form } from "components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useFilterActions } from "../../../ducks/filter/hooks/action";
import { useFilterState } from "../../../ducks/filter/hooks/filterState";
import { FilterStore } from "./FilterStore";
import { FilterDate } from "./FilterDate";
import { FilterOperationType } from "./FilterOperationType";
import { FilterPaymentType } from "./FilterPaymentType";
import { Filter, X } from "lucide-react";
import { toast } from "components/ui/use-toast";

const MainFiltersSchema = z.object({
  store: z.string({ required_error: "Loja é obrigatório." }).min(1, "Loja é obrigatório."),
  operationType: z.string({
    required_error: "Tipo de operação é obrigatório.",
  }).min(1, "Tipo de operação é obrigatório."),
  paymentType: z.string({
    required_error: "Tipo de pagamento é obrigatório.",
  }).min(1, "Tipo de pagamento é obrigatório."),
  date: z.string({ required_error: "Data é obrigatória." }).min(1, "Data é obrigatória.").refine(
    (val) => {
      const regex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
      return regex.test(val);
    },
    {
      message: "Data inválida. Use o formato dd/mm/aaaa.",
    }
  ).refine(
    (val) => {
      const parts = val.split("/");
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const year = parseInt(parts[2], 10);
      
      const selectedDate = new Date(year, month, day);
      const today = new Date();

      today.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);
      
      return selectedDate <= today;
    },
    {
      message: "Não é permitido selecionar uma data futura.",
    }
  ),
});

interface MainFiltersProps {
  storesData: any[];
}

export function MainFilters({ storesData }: MainFiltersProps) {
  const { updateFilter } = useFilterActions();
  const filterState = useFilterState();

  const form = useForm<z.infer<typeof MainFiltersSchema>>({
    resolver: zodResolver(MainFiltersSchema),
    defaultValues: {
      store: filterState?.filters?.advancedFilter?.store || "",
      date: filterState?.filters?.advancedFilter?.date || "",
      operationType: filterState?.filters?.advancedFilter?.operationType || "",
      paymentType: filterState?.filters?.advancedFilter?.paymentType || "",
    },
  });

  function onSubmit(data: z.infer<typeof MainFiltersSchema>) {
    updateFilter({
      key: "advancedFilter",
      value: {
        ...filterState?.filters?.advancedFilter,
        ...data,
      },
    });

    toast({
      title: "Filtros aplicados",
      description: "Os filtros foram aplicados com sucesso.",
      duration: 3000,
    });
  }

  function onError(errors: any) {
    const errorMessages = Object.values(errors)
      .map((error: any) => error.message)
      .filter(Boolean);

    if (errorMessages.length > 0) {
      toast({
        variant: "destructive",
        title: "Campos obrigatórios não preenchidos",
        description: errorMessages.join(", "),
        duration: 5000,
      });
    }
  }

  function handleClearFilters() {
    form.reset({
      store: "",
      date: "",
      operationType: "",
      paymentType: "",
    });

    const advancedFilters = filterState?.filters?.advancedFilter || {};
    const { store, date, operationType, paymentType, ...restFilters } =
      advancedFilters;

    updateFilter({
      key: "advancedFilter",
      value: restFilters,
    });

    toast({
      title: "Filtros limpos",
      description: "Todos os filtros obrigatórios foram limpos.",
      duration: 3000,
    });
  }

  return (
    <div className="bg-white rounded-lg border p-4 mb-4 shadow-sm">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, onError)}>
          <div className="flex flex-col gap-4">
            {/* Grid de filtros */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <FilterStore
                control={form.control}
                storesData={storesData}
                errors={form.formState.errors}
              />
              <FilterDate
                control={form.control}
                errors={form.formState.errors}
              />
              <FilterOperationType
                control={form.control}
                errors={form.formState.errors}
              />
              <FilterPaymentType
                control={form.control}
                errors={form.formState.errors}
              />
            </div>

            {/* Botões de ação */}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleClearFilters}
              >
                <X className="h-4 w-4 mr-2" />
                Limpar Filtros
              </Button>
              <Button type="submit" variant="default" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                Buscar
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}